import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Shell from "@/components/Shell";
import { toast } from "sonner";
import { Plus, Receipt, Download, Wallet, Banknote, HandCoins, Calculator, Heart, Search, Clock, Send } from "lucide-react";
import LoansTransfers from "@/pages/finance/LoansTransfers";
import BankOps from "@/pages/finance/BankOps";
import AlertsInbox from "@/pages/shared/AlertsInbox";

const TABS = [
  {id:"receipts", label:"Fee Receipts"},
  {id:"donations", label:"Donations"},
  {id:"expenses", label:"Expenses"},
  {id:"advances", label:"Salary Advances"},
  {id:"payroll", label:"Payroll"},
  {id:"loans", label:"Loans & Transfers"},
  {id:"bank", label:"Bank Actions"},
  {id:"alerts", label:"Alerts"},
];

export default function AccountantDashboard() {
  const [tab, setTab] = useState("receipts");
  return (
    <Shell title="Accountant Console" subtitle="Finance operations"
           tabs={TABS} active={tab} onTab={setTab}>
      {tab==="receipts" && <FeeReceipts/>}
      {tab==="donations" && <Donations/>}
      {tab==="expenses" && <Expenses/>}
      {tab==="advances" && <Advances/>}
      {tab==="payroll" && <Payroll/>}
      {tab==="loans" && <LoansTransfers mode="create"/>}
      {tab==="bank" && <BankOps role="accountant"/>}
      {tab==="alerts" && <AlertsInbox/>}
    </Shell>
  );
}

/* =========================================================
   Fee Receipts — Iter16: discount pulled from student record, no Discount input in form
========================================================= */
function FeeReceipts() {
  const [roll, setRoll] = useState("");
  const [preview, setPreview] = useState(null);
  const [alloc, setAlloc] = useState({admission:0, tuition:0, transport:0, hostel:0, others:0});
  const [method, setMethod] = useState("cash");
  const [note, setNote] = useState("");
  const [receipts, setReceipts] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [divisionId, setDivisionId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/receipts");
    setReceipts(r.data);
  };
  useEffect(()=>{ load(); api.get("/finance-settings/divisions").then(r=>setDivisions(r.data)).catch(()=>{}); }, []);

  const autoMapDivision = (stu) => {
    const g = stu?.class_name, s = stu?.section;
    const d = divisions.find(x => (x.grades||[]).includes(g) &&
              ((x.sections||[]).length===0 || (x.sections||[]).includes(s)))
              || divisions.find(x => (x.grades||[]).includes(g));
    setDivisionId(d?.division_id || "");
  };

  const lookup = async () => {
    if (!roll.trim()) return;
    setBusy(true);
    try {
      const r = await api.get("/receipts/preview/by-roll", { params: { roll_no: roll.trim() }});
      setPreview(r.data);
      autoMapDivision(r.data.student);
      // pre-fill allocation to full Net Payable Balance (bias toward Tuition)
      const npb = r.data.net_payable_balance ?? r.data.net_payable ?? 0;
      setAlloc({admission:0, tuition: Math.max(0, npb), transport:0, hostel:0, others:0});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Student not found");
      setPreview(null);
    }
    setBusy(false);
  };

  const total = ["admission","tuition","transport","hostel","others"]
    .reduce((a,k)=>a + (parseFloat(alloc[k])||0), 0);
  const grossTotal = preview?.gross_total || 0;
  const discTotal = preview?.discount_amount || 0;
  const netTotal = preview?.net_structured_total ?? preview?.net_after_discount ?? 0;
  const historicalDues = preview?.historical_dues || 0;
  const historicalPayments = preview?.historical_payments ?? preview?.previous_payments ?? 0;
  const totalDues = preview?.total_dues ?? (netTotal + historicalDues);
  const netPayableBalance = preview?.net_payable_balance ?? preview?.net_payable ?? (totalDues - historicalPayments);
  const outstanding = netPayableBalance - total;

  const commit = async () => {
    if (!preview) return;
    if (total <= 0) return toast.error("Allocate at least ₹1 across categories");
    if (!divisionId) return toast.error("Select a Division/Branch for this receipt");
    setBusy(true);
    try {
      const div = divisions.find(d=>d.division_id===divisionId);
      const r = await api.post("/receipts", {
        student_id: preview.student.student_id,
        allocation: {
          admission: parseFloat(alloc.admission||0),
          tuition:   parseFloat(alloc.tuition||0),
          transport: parseFloat(alloc.transport||0),
          hostel:    parseFloat(alloc.hostel||0),
          others:    parseFloat(alloc.others||0),
        },
        division_id: divisionId, division_name: div?.name || null,
        method, note,
      });
      toast.success(`Receipt ${r.data.receipt_no} created · ₹${r.data.amount}`);
      const pdf = await api.get(`/fees/receipt/${r.data.receipt_id}`, { responseType: "blob" });
      window.open(URL.createObjectURL(pdf.data), "_blank");
      setRoll(""); setPreview(null); setNote(""); setDivisionId("");
      setAlloc({admission:0, tuition:0, transport:0, hostel:0, others:0});
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const downloadPdf = async (rid, rno) => {
    const r = await api.get(`/fees/receipt/${rid}`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = `${rno || rid}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div className="space-y-5">
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="nb-card p-6 bg-[#FDE047] space-y-3">
          <h3 className="display text-xl font-bold flex items-center gap-2">
            <Calculator size={18}/>New Fee Receipt
          </h3>
          <div className="text-xs">Enter the student&apos;s Roll Number — Gross fees, per-line Discount (from the student&apos;s assigned Discount), Net Fee, Historical Dues &amp; Historical Payments are auto-computed.</div>
          <div className="flex gap-2">
            <input className="nb-input flex-1" placeholder="Roll number (e.g. 10A01)"
                   value={roll} onChange={e=>setRoll(e.target.value)}
                   onKeyDown={e=>e.key==="Enter" && lookup()}
                   data-testid="rcpt-roll"/>
            <button onClick={lookup} disabled={busy} className="nb-btn"
                    data-testid="rcpt-lookup"><Search size={14}/>Lookup</button>
          </div>
          {preview && (
            <div className="nb-card-sm p-2 bg-white text-sm" data-testid="rcpt-student-info">
              <b>{preview.student.name}</b> · {preview.student.class_name}-{preview.student.section} ·
              Roll {preview.student.roll_no} · Parent: {preview.student.parent_name}
              {preview.discount_percent > 0 && (
                <span className="ml-2 nb-badge bg-[#86EFAC] text-[10px]">
                  Discount: {preview.discount_type} · {preview.discount_percent}%
                </span>
              )}
            </div>
          )}
        </div>

        {preview && (
          <div className="nb-card p-6 bg-white space-y-2" data-testid="rcpt-preview">
            <h3 className="display text-xl font-bold">Live Preview</h3>
            <div className="text-sm space-y-1 mono">
              {/* 4-col fee table: Type · Gross · Discount · Net Fee */}
              <div className="grid grid-cols-[1fr_1fr_1fr_1fr] gap-2 font-bold text-xs border-b-2 border-black pb-1">
                <span>Fee Type</span>
                <span className="text-right">Gross</span>
                <span className="text-right">Discount</span>
                <span className="text-right">Net Fee</span>
              </div>
              {preview.items.map(it => (
                <div key={it.key} className="grid grid-cols-[1fr_1fr_1fr_1fr] gap-2 text-xs" data-testid={`prev-row-${it.key}`}>
                  <span>{it.label}</span>
                  <span className="text-right">₹{(it.amount||0).toLocaleString()}</span>
                  <span className={"text-right " + ((it.discount||0)>0?"text-emerald-700":"text-slate-400")}>
                    {(it.discount||0)>0?`₹${(it.discount||0).toLocaleString()}`:"—"}
                  </span>
                  <span className="text-right font-bold">₹{(it.net||0).toLocaleString()}</span>
                </div>
              ))}
              <div className="grid grid-cols-[1fr_1fr_1fr_1fr] gap-2 font-bold text-xs border-t-2 border-black pt-1 bg-[#FDE047]"
                   data-testid="prev-column-totals">
                <span>Totals</span>
                <span className="text-right">₹{grossTotal.toLocaleString()}</span>
                <span className="text-right text-emerald-700">₹{discTotal.toLocaleString()}</span>
                <span className="text-right">₹{netTotal.toLocaleString()}</span>
              </div>
              {/* Sequential ledger — Iter17 exact order. Historical Payments &
                  Amount Paid Now are shown positively (they subtract implicitly
                  from Total Dues → Net Payable Balance → Outstanding). */}
              <div className="border-t-2 border-black mt-2"/>
              <Row label="Total Net Fee" v={netTotal} bold/>
              <Row label="Historical Dues (carried over)" v={historicalDues}/>
              <Row label="Total Dues Payable" v={totalDues} bold/>
              <Row label="Historical Payments (this cycle)" v={historicalPayments} deduct/>
              <Row label="Net Payable Balance" v={netPayableBalance} bold signed/>
              <Row label="Amount Paid Now" v={total} deduct/>
              <div className="border-t-2 border-black"/>
              <Row label="Outstanding Balance Payable" v={outstanding} bold signed/>
              {(preview.due_date || preview.final_academic_deadline) && (
                <div className="text-[10px] text-slate-600 mt-1 space-y-0.5" data-testid="prev-due-note">
                  {preview.due_date && <div><b>Note:</b> Next monthly due: {preview.due_date}</div>}
                  {preview.final_academic_deadline && <div>Final academic deadline: {preview.final_academic_deadline}</div>}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {preview && (
        <div className="nb-card p-6 bg-[#93C5FD] space-y-3" data-testid="rcpt-allocation">
          <h3 className="display text-xl font-bold">Payment Allocation (per fee type)</h3>
          <p className="text-xs">Split this transaction across the exact categories being paid. Discounts are pulled from the student&apos;s record — enter <b>Amount Paid Now</b> per category.</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              ["admission","Admission"], ["tuition","Tuition"],
              ["transport","Transport"], ["hostel","Hostel"], ["others","Others"],
            ].map(([k, lbl]) => (
              <label key={k}>
                <span className="nb-label">{lbl}</span>
                <input type="number" min="0" step="0.01" className="nb-input mt-1 mono"
                       value={alloc[k]}
                       onChange={e=>setAlloc({...alloc, [k]: e.target.value})}
                       data-testid={`rcpt-alloc-${k}`}/>
              </label>
            ))}
          </div>
          <div className="grid md:grid-cols-3 gap-3 pt-2 border-t-2 border-black">
            <label><span className="nb-label">Division / Branch *</span>
              <select className="nb-select mt-1" value={divisionId}
                      onChange={e=>setDivisionId(e.target.value)} data-testid="rcpt-division">
                <option value="">Select division…</option>
                {divisions.map(d => <option key={d.division_id} value={d.division_id}>{d.name}</option>)}
              </select>
              <span className="text-[11px] text-slate-500">Auto-mapped from Grade/Section · override if needed</span>
            </label>
            <label><span className="nb-label">Method</span>
              <select className="nb-select mt-1" value={method}
                      onChange={e=>setMethod(e.target.value)} data-testid="rcpt-method">
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="bank">Bank</option>
                <option value="cheque">Cheque</option>
              </select></label>
            <label><span className="nb-label">Note (optional)</span>
              <input className="nb-input mt-1" value={note}
                     onChange={e=>setNote(e.target.value)} data-testid="rcpt-note"/></label>
          </div>
          <div className="grid md:grid-cols-4 gap-3 pt-2">
            <div className="nb-card-sm p-3 bg-white">
              <div className="nb-label">Gross</div>
              <div className="mono font-bold text-xl">₹{grossTotal.toLocaleString()}</div>
            </div>
            <div className="nb-card-sm p-3 bg-white">
              <div className="nb-label">Receipt Total</div>
              <div className="mono font-bold text-2xl">₹{Math.max(0,total).toLocaleString()}</div>
            </div>
            <div className="nb-card-sm p-3 bg-white">
              <div className="nb-label">Balance After</div>
              <div className="mono font-bold text-2xl">₹{Math.max(0, outstanding).toLocaleString()}</div>
            </div>
            <button onClick={commit} disabled={busy || total<=0}
                    className="nb-btn justify-center text-lg" data-testid="rcpt-generate">
              <Receipt size={16}/>Generate Receipt + Print
            </button>
          </div>
        </div>
      )}

      <div className="nb-card p-0 overflow-hidden">
        <div className="px-6 py-3 border-b-2 border-black bg-white flex justify-between">
          <h3 className="display text-lg font-bold">Recent Fee Receipts</h3>
          <span className="text-xs mono">Total: {receipts.length}</span>
        </div>
        <table className="nb-table">
          <thead><tr><th>Receipt No</th><th>Date</th><th>Student</th><th>Method</th><th>Amount</th><th></th></tr></thead>
          <tbody>
            {receipts.slice(0, 40).map(r=>(
              <tr key={r.receipt_id} data-testid={`rcpt-row-${r.receipt_id}`}>
                <td className="mono font-bold">{r.receipt_no || "—"}</td>
                <td className="mono text-xs">{(r.paid_at||"").slice(0,10)}</td>
                <td>{r.student_name || r.student_id}</td>
                <td>{(r.method||"").toUpperCase()}</td>
                <td className="mono font-bold">₹{(r.amount||0).toLocaleString()}</td>
                <td>
                  <button onClick={()=>downloadPdf(r.receipt_id, r.receipt_no)}
                          className="nb-btn nb-btn-secondary px-2 py-1 text-xs"
                          data-testid={`rcpt-pdf-${r.receipt_id}`}>
                    <Download size={12}/> PDF
                  </button>
                </td>
              </tr>
            ))}
            {receipts.length===0 && <tr><td colSpan={6} className="text-center py-6 text-slate-500">No receipts yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Row({label, v, bold, negative, deduct, signed}) {
  // Iter18:
  //   `deduct` — an informational payment row (Historical Payments / Amount Paid Now).
  //             The math subtracts these upstream — display prefixed "less" and rose color,
  //             without a negative sign on the value itself.
  //   `signed` — running-balance rows (Net Payable Balance / Outstanding). Real sign
  //             preserved so refunds (school-owes-parent) show as -₹X.
  //   `negative` — legacy; treated like `deduct`.
  const val = v || 0;
  let display;
  let cls = "";
  if (signed) {
    display = `${val < 0 ? "-" : ""}₹${Math.abs(val).toLocaleString()}`;
    if (val < 0) cls = "text-emerald-700";
  } else if (deduct || negative) {
    display = `less ₹${Math.abs(val).toLocaleString()}`;
    cls = "text-rose-700";
  } else {
    display = `₹${Math.abs(val).toLocaleString()}`;
  }
  return <div className={`flex justify-between ${bold?"font-bold":""}`}>
    <span>{label}</span>
    <span className={cls}>{display}</span>
  </div>;
}

/* =========================================================
   Donations — moved from Admin. Saves entry visible to Admin + Founder.
========================================================= */
function Donations() {
  const [rows, setRows] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const empty = {donor_name:"", donor_email:"", mobile_number:"", division_id:"", amount:"", message:""};
  const [f, setF] = useState(empty);
  const load = () => api.get("/donations").then(r=>setRows(r.data));
  useEffect(()=>{ load(); api.get("/finance-settings/divisions").then(r=>setDivisions(r.data)).catch(()=>{}); }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (f.mobile_number && !/^\d{10}$/.test(f.mobile_number)) return toast.error("Mobile number must be 10 digits");
    try {
      const div = divisions.find(d=>d.division_id===f.division_id);
      await api.post("/donations", {...f, amount: parseFloat(f.amount||0),
        mobile_number: f.mobile_number||null, division_id: f.division_id||null,
        division_name: div?.name||null});
      toast.success("Donation recorded · visible on Admin & Founder consoles");
      setF(empty); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={submit} className="nb-card p-6 bg-[#FDE047] space-y-3 h-fit">
        <h3 className="display text-xl font-bold flex items-center gap-2"><Heart size={18}/>Record Donation</h3>
        <input className="nb-input" placeholder="Donor Name" required
               value={f.donor_name} onChange={e=>setF({...f, donor_name:e.target.value})}
               data-testid="don-name"/>
        <input className="nb-input" placeholder="Donor Email (optional)"
               value={f.donor_email} onChange={e=>setF({...f, donor_email:e.target.value})}
               data-testid="don-email"/>
        <input inputMode="numeric" className="nb-input mono" placeholder="Mobile Number (optional, 10 digits)"
               value={f.mobile_number}
               onChange={e=>setF({...f, mobile_number:e.target.value.replace(/\D/g,"").slice(0,10)})}
               data-testid="don-mobile"/>
        <select className="nb-select" value={f.division_id}
                onChange={e=>setF({...f, division_id:e.target.value})} data-testid="don-division">
          <option value="">Division / Branch (optional)</option>
          {divisions.map(d => <option key={d.division_id} value={d.division_id}>{d.name}</option>)}
        </select>
        <input type="number" className="nb-input mono" placeholder="Amount ₹" required
               value={f.amount} onChange={e=>setF({...f, amount:e.target.value})}
               data-testid="don-amount"/>
        <textarea className="nb-input" placeholder="Message / dedication" rows="3"
                  value={f.message} onChange={e=>setF({...f, message:e.target.value})}
                  data-testid="don-message"/>
        <button className="nb-btn w-full justify-center" data-testid="don-submit">
          <Plus size={16}/> Save Donation
        </button>
      </form>
      <div className="nb-card p-0 overflow-hidden lg:col-span-2">
        <div className="px-6 py-3 border-b-2 border-black bg-white">
          <h3 className="display text-lg font-bold">All Donations (read-only log)</h3>
        </div>
        <table className="nb-table">
          <thead><tr><th>Date</th><th>Donor</th><th>Mobile</th><th>Division</th><th>Amount</th><th>Message</th><th></th></tr></thead>
          <tbody>
            {rows.map(r=>(
              <tr key={r.donation_id}>
                <td className="mono text-xs">{(r.created_at||"").slice(0,10)}</td>
                <td className="font-bold">{r.donor_name}</td>
                <td className="mono text-xs">{r.mobile_number||"—"}</td>
                <td className="text-xs">{r.division_name||"—"}</td>
                <td className="mono">₹{(r.amount||0).toLocaleString()}</td>
                <td className="text-sm">{r.message||"—"}</td>
                <td>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/donations/${r.donation_id}/certificate`}
                     target="_blank" rel="noreferrer"
                     className="nb-btn nb-btn-secondary px-2 py-1 text-xs">
                    <Download size={12}/> Cert
                  </a>
                </td>
              </tr>
            ))}
            {rows.length===0 && <tr><td colSpan={7} className="text-center py-6 text-slate-500">No donations yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// Invoice: grade + roll → student lookup; fee type dropdown filtering structures;
// real-time preview with full 7-step math, then create + auto-record initial payment

function Expenses() {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [f, setF] = useState({category:"", amount:"", description:"",
                              vendor_name:"", payee_mobile:"", division_id:"", remarks:""});
  const load = async () => {
    setRows((await api.get("/expenses")).data);
    const s = (await api.get("/settings")).data;
    setCats(s.expense_categories || []);
    if (!f.category && s.expense_categories?.length) setF(x=>({...x, category:s.expense_categories[0]}));
    try { setDivisions((await api.get("/finance-settings/divisions")).data); } catch { /* not admin scope */ }
  };
  useEffect(()=>{ load(); }, []);  // eslint-disable-line
  const submit = async (e) => { e.preventDefault();
    const r = await api.post("/expenses", {...f, amount:parseFloat(f.amount)});
    toast[r.data.high_priority?"warning":"success"](r.data.high_priority?"Logged as HIGH PRIORITY":"Expense logged");
    setF({...f, amount:"", description:"", vendor_name:"", payee_mobile:"", remarks:""}); load();
  };
  const thisMonth = new Date().toISOString().slice(0,7);
  const monthlyTotal = rows
    .filter(r => (r.created_at||"").slice(0,7) === thisMonth)
    .reduce((sum, r) => sum + parseFloat(r.amount||0), 0);
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={submit} className="nb-card p-6 bg-[#FCA5A5] space-y-3 h-fit"
            data-testid="acc-expense-form">
        <h3 className="display text-xl font-bold">Log Expense</h3>
        <select required className="nb-select" value={f.category} onChange={e=>setF({...f,category:e.target.value})} data-testid="exp-category">
          {cats.map(c=><option key={c} value={c}>{c}</option>)}
        </select>
        <input type="number" step="0.01" className="nb-input" placeholder="Amount" required value={f.amount} onChange={e=>setF({...f,amount:e.target.value})}/>
        <input className="nb-input" placeholder="Vendor / Subcontractor Name"
               value={f.vendor_name} onChange={e=>setF({...f,vendor_name:e.target.value})}
               data-testid="exp-vendor"/>
        <input className="nb-input" placeholder="Payee Mobile Number"
               value={f.payee_mobile} onChange={e=>setF({...f,payee_mobile:e.target.value})}
               data-testid="exp-mobile"/>
        <select className="nb-select" value={f.division_id}
                onChange={e=>setF({...f,division_id:e.target.value})}
                data-testid="exp-division">
          <option value="">— Division / Branch —</option>
          {divisions.map(d => <option key={d.division_id} value={d.division_id}>{d.name}</option>)}
        </select>
        <textarea className="nb-textarea" placeholder="Description" value={f.description} onChange={e=>setF({...f,description:e.target.value})}/>
        <input className="nb-input" placeholder="Remarks (shown on Print Summary)"
               value={f.remarks} onChange={e=>setF({...f,remarks:e.target.value})}
               data-testid="exp-remarks"/>
        <button className="nb-btn w-full justify-center"><Plus size={16}/>Submit</button>
      </form>
      <div className="lg:col-span-2 space-y-3">
        <div className="nb-card p-4 bg-[#FDE047] flex items-center justify-between"
             data-testid="acc-expense-monthly">
          <div>
            <div className="nb-label">Monthly Total Expenses — {thisMonth}</div>
            <div className="display text-2xl font-black">₹{monthlyTotal.toLocaleString()}</div>
          </div>
          <div className="text-xs text-slate-700">{rows.filter(r=>(r.created_at||'').slice(0,7)===thisMonth).length} entries</div>
        </div>
        <div className="nb-card p-0 overflow-hidden">
          <table className="nb-table"><thead><tr><th>Date</th><th>Category</th><th>Vendor</th><th>Amount</th><th>Status</th></tr></thead><tbody>
            {rows.map(r=>(
              <tr key={r.expense_id}><td className="mono text-xs">{r.created_at.slice(0,10)}</td>
                <td>{r.category}</td>
                <td className="text-xs">{r.vendor_name || r.description || "—"}</td>
                <td className="mono">₹{r.amount} {r.high_priority && <span className="nb-badge bg-[#FCA5A5] ml-1">HIGH</span>}</td>
                <td><span className={`nb-badge ${r.status==="approved"?"bg-[#86EFAC]":r.status==="rejected"?"bg-[#FCA5A5]":"bg-[#FDE047]"}`}>{r.status.toUpperCase()}</span></td></tr>
            ))}
          </tbody></table>
        </div>
      </div>
    </div>
  );
}

function Advances() {
  const [staff, setStaff] = useState([]);
  const [advs, setAdvs] = useState([]);
  const [f, setF] = useState({beneficiary_id:"", beneficiary_kind:"", amount:"", installments:3});
  const load = async () => {
    setStaff((await api.get("/staff").catch(()=>({data:[]}))).data);
    setAdvs((await api.get("/advances")).data);
  };
  useEffect(()=>{ load(); }, []);
  const pickStaff = (bid) => {
    const s = staff.find(x=>x.beneficiary_id===bid);
    setF({...f, beneficiary_id: bid, beneficiary_kind: s?.kind || ""});
  };
  const submit = async (e) => { e.preventDefault();
    if (!f.beneficiary_id) return toast.error("Pick a staff member");
    await api.post("/advances", {
      beneficiary_id: f.beneficiary_id,
      beneficiary_kind: f.beneficiary_kind,
      amount: parseFloat(f.amount),
      installments: parseInt(f.installments),
    });
    toast.success("Advance recorded"); load();
  };
  const nameOf = (a) => a.beneficiary_name ||
    staff.find(x=>x.beneficiary_id===(a.beneficiary_id||a.educator_id))?.name || "—";
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={submit} className="nb-card p-6 bg-[#C4B5FD] space-y-3 h-fit">
        <h3 className="display text-xl font-bold">Salary Advance</h3>
        <div className="text-xs">Pulls the full staff roster from the School Admin&apos;s <b>Staff &amp; Attendance</b> tab.</div>
        <select required className="nb-select" value={f.beneficiary_id}
                onChange={e=>pickStaff(e.target.value)}
                data-testid="adv-staff">
          <option value="">-- Staff Member --</option>
          {staff.map(s=>(
            <option key={`${s.kind}|${s.beneficiary_id}`} value={s.beneficiary_id}>
              {s.name} · {s.designation} · Basic ₹{s.monthly_salary||0}
            </option>
          ))}
        </select>
        <input type="number" className="nb-input" placeholder="Amount" required
               value={f.amount} onChange={e=>setF({...f,amount:e.target.value})}
               data-testid="adv-amount"/>
        <select className="nb-select" value={f.installments}
                onChange={e=>setF({...f,installments:e.target.value})}
                data-testid="adv-installments">
          {[1,2,3,4,5,6,9,12].map(n => <option key={n} value={n}>{n} installment{n>1?"s":""}</option>)}
        </select>
        <button className="nb-btn w-full justify-center" data-testid="adv-submit">
          <HandCoins size={16}/>Approve Advance
        </button>
      </form>
      <div className="nb-card p-0 overflow-hidden lg:col-span-2">
        <table className="nb-table"><thead><tr><th>Staff</th><th>Amount</th><th>Per Inst.</th><th>Progress</th><th>Status</th></tr></thead><tbody>
          {advs.map(a=>(
            <tr key={a.advance_id}><td>{nameOf(a)}</td><td className="mono">₹{a.amount}</td>
              <td className="mono">₹{a.per_installment}</td><td className="mono">{a.paid_count}/{a.installments}</td>
              <td><span className="nb-badge">{a.status}</span></td></tr>
          ))}
        </tbody></table>
      </div>
    </div>
  );
}

function Payroll() {
  const [staff, setStaff] = useState([]);
  const [runs, setRuns] = useState([]);
  const [batches, setBatches] = useState([]);
  const [monthFilter, setMonthFilter] = useState("");   // Iter24 — filter payroll grid by month
  const [bulkMonth, setBulkMonth] = useState(new Date().toISOString().slice(0,7));
  const [f, setF] = useState({beneficiary_id:"", beneficiary_kind:"", month: new Date().toISOString().slice(0,7), bonus:0, other_deduction:0});
  const load = async () => {
    setStaff((await api.get("/staff").catch(()=>({data:[]}))).data);
    setRuns((await api.get("/payroll")).data);
    try { setBatches((await api.get("/payroll/batches")).data); } catch { /* silent */ }
  };
  useEffect(()=>{ load(); }, []);
  const pickStaff = (bid) => {
    const s = staff.find(x=>x.beneficiary_id===bid);
    setF({...f, beneficiary_id: bid, beneficiary_kind: s?.kind || ""});
  };
  const run = async (e) => { e.preventDefault();
    if (!f.beneficiary_id) return toast.error("Pick a staff member");
    const r = await api.post("/payroll/run", {
      beneficiary_id: f.beneficiary_id, beneficiary_kind: f.beneficiary_kind,
      month: f.month, bonus:parseFloat(f.bonus||0), other_deduction:parseFloat(f.other_deduction||0)
    });
    toast.success(`Net ₹${r.data.net} · Leaves ${r.data.leave_days} · Adv ₹${r.data.advance_deduction}`); load();
  };
  const push = async (pid) => {
    try {
      await api.post(`/payroll/${pid}/push-approval`);
      toast.success("Sent for Admin approval");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to push"); }
  };
  const bulkPush = async () => {
    if (!bulkMonth) return toast.error("Pick a month");
    if (!window.confirm(`Push ALL pending payrolls of ${bulkMonth} for approval?`)) return;
    try {
      const r = await api.post("/payroll/bulk-push-approval", {month: bulkMonth});
      toast.success(`Batch pushed · ${r.data.count} payrolls submitted for approval`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Bulk push failed"); }
  };
  const pay = async (pid) => {
    try {
      await api.post(`/payroll/${pid}/pay`);
      toast.success("Marked as Paid");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const printSlip = async (pid, name, month) => {
    try {
      const res = await api.get(`/payroll/${pid}/payslip-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `payslip_${(name||"staff").replace(/\s+/g,"_")}_${month}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(err?.response?.data?.detail || "Payslip locked until approval"); }
  };
  const statusBadge = (st) => {
    const cls = {
      "pending": "bg-[#FDE047]",
      "pending_approval": "bg-[#FDBA74]",
      "approved": "bg-[#93C5FD]",
      "paid": "bg-[#86EFAC]",
    }[st] || "bg-white";
    const label = {
      "pending": "COMPUTED",
      "pending_approval": "AWAITING APPROVAL",
      "approved": "APPROVED",
      "paid": "PAID",
    }[st] || (st||"").toUpperCase();
    return <span className={`nb-badge ${cls}`}>{label}</span>;
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={run} className="nb-card p-6 bg-[#93C5FD] space-y-3 h-fit">
        <h3 className="display text-xl font-bold">Run Payroll</h3>
        <div className="text-xs">Base salary + monthly attendance are pulled from the Admin&apos;s <b>Staff &amp; Attendance</b> tab. Additions (HRA/DA/TA) &amp; Deductions (PF/Gratuity) come from the assigned <b>Salary Slab</b> in Admin Settings.</div>
        <select required className="nb-select" value={f.beneficiary_id}
                onChange={e=>pickStaff(e.target.value)}
                data-testid="pay-staff">
          <option value="">-- Staff Member --</option>
          {staff.map(s=>(
            <option key={`${s.kind}|${s.beneficiary_id}`} value={s.beneficiary_id}>
              {s.name} · {s.designation} · Basic ₹{s.monthly_salary||0}/mo
            </option>
          ))}
        </select>
        <input type="month" className="nb-input" value={f.month} onChange={e=>setF({...f,month:e.target.value})} data-testid="pay-month"/>
        <input type="number" className="nb-input" placeholder="Bonus" value={f.bonus} onChange={e=>setF({...f,bonus:e.target.value})}/>
        <input type="number" className="nb-input" placeholder="Other deduction" value={f.other_deduction} onChange={e=>setF({...f,other_deduction:e.target.value})}/>
        <button className="nb-btn w-full justify-center" data-testid="pay-submit"><Banknote size={16}/>Calculate</button>
        <div className="text-[11px] text-slate-800">
          <b>Approval Flow:</b> Compute → <b>Push for Approvals</b> → Admin/Founder approves → <b>Print PDF</b> unlocks → Mark Paid.
        </div>
      </form>
      <div className="nb-card p-0 overflow-hidden lg:col-span-2">
        <div className="flex flex-wrap items-center gap-2 p-3 border-b-2 border-black bg-[#FDE047]"
             data-testid="pay-bulk-bar">
          <span className="nb-label">Filter Month:</span>
          <input type="month" className="nb-input text-xs w-40"
                 value={monthFilter}
                 onChange={e=>setMonthFilter(e.target.value)}
                 data-testid="pay-month-filter"/>
          {monthFilter && <button className="nb-btn nb-btn-ghost text-xs" onClick={()=>setMonthFilter("")}>Clear</button>}
          <span className="mx-2 border-l-2 border-black h-6"/>
          <span className="nb-label">Bulk Push Month:</span>
          <input type="month" className="nb-input text-xs w-40"
                 value={bulkMonth} onChange={e=>setBulkMonth(e.target.value)}
                 data-testid="pay-bulk-month"/>
          <button className="nb-btn" onClick={bulkPush}
                  data-testid="pay-bulk-push">
            <Send size={14}/> Bulk Push for Approval
          </button>
        </div>
        {batches.length > 0 && (
          <div className="p-3 border-b-2 border-black bg-white text-xs" data-testid="pay-batch-log">
            <div className="nb-label mb-1">Monthly Batch Log</div>
            <div className="flex flex-wrap gap-2">
              {batches.slice(0,6).map(b => (
                <span key={b.batch_id} className={`nb-badge ${b.status==="approved"?"bg-[#86EFAC]":"bg-[#FDBA74]"}`}
                      data-testid={`pay-batch-${b.month}`}>
                  {b.month} · {b.count} rows · {(b.status||"").toUpperCase().replace("_"," ")}
                </span>
              ))}
            </div>
          </div>
        )}
        <table className="nb-table">
          <thead><tr><th>Staff</th><th>Month</th><th>Base</th><th>Leaves</th><th>Advance</th><th>Net</th><th>Status</th><th>Action</th></tr></thead>
          <tbody>
          {runs.filter(r => !monthFilter || r.month === monthFilter).map(r=>{
            const st = r.status || "pending";
            const canPush = st === "pending" || !st;
            const canPrint = st === "approved" || st === "paid";
            const canPay = st === "approved";
            return (
              <tr key={r.payroll_id} data-testid={`pay-row-${r.payroll_id}`}>
                <td>{r.beneficiary_name || r.educator_name}</td>
                <td className="mono">{r.month}</td>
                <td className="mono">₹{r.base}</td>
                <td className="mono">{r.leave_days} (₹{r.leave_deduction})</td>
                <td className="mono">₹{r.advance_deduction}</td>
                <td className="mono font-bold">₹{r.net}</td>
                <td>{statusBadge(st)}</td>
                <td className="space-x-1 whitespace-nowrap">
                  {canPush && (
                    <button onClick={()=>push(r.payroll_id)}
                            className="nb-btn nb-btn-tertiary text-xs"
                            data-testid={`pay-push-${r.payroll_id}`}>
                      <Send size={12}/> Push for Approvals
                    </button>
                  )}
                  {canPrint && (
                    <button onClick={()=>printSlip(r.payroll_id, r.beneficiary_name || r.educator_name, r.month)}
                            className="nb-btn nb-btn-secondary text-xs"
                            data-testid={`pay-print-${r.payroll_id}`}>
                      <Download size={12}/> Print PDF
                    </button>
                  )}
                  {canPay && (
                    <button onClick={()=>pay(r.payroll_id)}
                            className="nb-btn text-xs"
                            data-testid={`pay-mark-paid-${r.payroll_id}`}>
                      <Wallet size={12}/> Mark Paid
                    </button>
                  )}
                  {st === "pending_approval" && (
                    <span className="text-[10px] text-slate-500 italic">Awaiting Admin/Founder</span>
                  )}
                </td>
              </tr>
            );
          })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
